// src/services/balance_ch.js
import api from "./api";
import { staleWhileRevalidate } from "./cache";

const stableKey = (prefix, obj) => {
  const o = { ...obj };
  if (Array.isArray(o.compIds)) o.compIds = [...o.compIds].sort();
  return `${prefix}:${JSON.stringify(o)}`;
};
const withoutSignal = (opts = {}) => {
  const { signal, ...rest } = opts || {};
  return rest;
};

export const getBsSummary = async ({ from, to, compIds = [], signal } = {}) => {
  const params = new URLSearchParams();
  if (from) params.set("from_date", from);
  if (to) params.set("to_date", to);
  if (Array.isArray(compIds) && compIds.length) params.set("comp_ids", compIds.join(","));
  const { data } = await api.get(`/dash/bs/summary?${params.toString()}`, { signal });
  return data;
};

export const getBsSummaryCached = async (opts = {}, { ttlMs = 5 * 60 * 1000, onUpdate } = {}) => {
  const key = stableKey('bs_summary', { from: opts.from, to: opts.to, compIds: opts.compIds });
  return staleWhileRevalidate({ key, ttlMs, onUpdate, signal: opts.signal, fetcher: () => getBsSummary(withoutSignal(opts)) });
};

export const getBsKpis = async ({ from, to, compIds = [], signal } = {}) => {
  const params = new URLSearchParams();
  if (from) params.set("from_date", from);
  if (to) params.set("to_date", to);
  if (Array.isArray(compIds) && compIds.length) params.set("comp_ids", compIds.join(","));
  const { data } = await api.get(`/dash/bs/kpis?${params.toString()}`, { signal });
  return data;
};

export const getBsKpisCached = async (opts = {}, { ttlMs = 5 * 60 * 1000, onUpdate } = {}) => {
  const key = stableKey('bs_kpis', { from: opts.from, to: opts.to, compIds: opts.compIds });
  return staleWhileRevalidate({ key, ttlMs, onUpdate, signal: opts.signal, fetcher: () => getBsKpis(withoutSignal(opts)) });
};

export const getBsDetail = async ({ line, from, to, compIds = [], signal } = {}) => {
  const params = new URLSearchParams();
  if (line) params.set("line", line);
  if (from) params.set("from_date", from);
  if (to) params.set("to_date", to);
  if (Array.isArray(compIds) && compIds.length) params.set("comp_ids", compIds.join(","));
  const { data } = await api.get(`/dash/bs/detail?${params.toString()}`, { signal });
  return data;
};

export const getBsDetailCached = async (opts = {}, { ttlMs = 5 * 60 * 1000, onUpdate } = {}) => {
  const key = stableKey('bs_detail', { line: opts.line, from: opts.from, to: opts.to, compIds: opts.compIds });
  return staleWhileRevalidate({ key, ttlMs, onUpdate, signal: opts.signal, fetcher: () => getBsDetail(withoutSignal(opts)) });
};

export const getBsObLines = async ({ ledger, from, compIds = [], signal } = {}) => {
  const params = new URLSearchParams();
  if (ledger) params.set("ledger", ledger);
  if (from) params.set("from_date", from);
  if (Array.isArray(compIds) && compIds.length) params.set("comp_ids", compIds.join(","));
  const { data } = await api.get(`/dash/bs/ob-lines?${params.toString()}`, { signal });
  return data;
};

export const getBsMovementTxns = async ({ ledger, from, to, compIds = [], signal } = {}) => {
  const params = new URLSearchParams();
  if (ledger) params.set("ledger", ledger);
  if (from) params.set("from_date", from);
  if (to) params.set("to_date", to);
  if (Array.isArray(compIds) && compIds.length) params.set("comp_ids", compIds.join(","));
  const { data } = await api.get(`/dash/bs/mv-txns?${params.toString()}`, { signal });
  return data;
};

export const getBsLastDate = async ({ compIds = [], signal } = {}) => {
  const params = new URLSearchParams();
  if (Array.isArray(compIds) && compIds.length) params.set("comp_ids", compIds.join(","));
  const { data } = await api.get(`/dash/bs/last-date?${params.toString()}`, { signal });
  return data;
};

export const getBsReconciliationDifferences = async ({ from, to, compIds = [], signal } = {}) => {
  const params = new URLSearchParams();
  if (from) params.set("from_date", from);
  if (to) params.set("to_date", to);
  if (Array.isArray(compIds) && compIds.length) params.set("comp_ids", compIds.join(","));
  const { data } = await api.get(`/dash/bs/reconciliation-differences?${params.toString()}`, { signal });
  return data;
};

export const getBsReconciliationDifferencesCached = async (opts = {}, { ttlMs = 5 * 60 * 1000, onUpdate } = {}) => {
  const key = stableKey('bs_reconciliation_differences', { from: opts.from, to: opts.to, compIds: opts.compIds });
  return staleWhileRevalidate({
    key,
    ttlMs,
    onUpdate,
    signal: opts.signal,
    fetcher: () => getBsReconciliationDifferences(withoutSignal(opts)),
  });
};

export const getBsPrimaryGroups = async ({ line, from, to, compIds = [] } = {}) => {
  const params = new URLSearchParams();
  if (line) params.set("line", line);
  if (from) params.set("from_date", from);
  if (to) params.set("to_date", to);
  if (Array.isArray(compIds) && compIds.length) params.set("comp_ids", compIds.join(","));
  const { data } = await api.get(`/dash/bs/primary-groups?${params.toString()}`);
  return data;
};

export const getBsParentOfParentGroups = async ({ line, primaryGroup, from, to, compIds = [] } = {}) => {
  const params = new URLSearchParams();
  if (line) params.set("line", line);
  if (primaryGroup) params.set("primary_group", primaryGroup);
  if (from) params.set("from_date", from);
  if (to) params.set("to_date", to);
  if (Array.isArray(compIds) && compIds.length) params.set("comp_ids", compIds.join(","));
  const { data } = await api.get(`/dash/bs/parent-of-parent-groups?${params.toString()}`);
  return data;
};

export const getBsParentGroups = async ({ line, primaryGroup, parentOfParentGroup, from, to, compIds = [] } = {}) => {
  const params = new URLSearchParams();
  if (line) params.set("line", line);
  if (primaryGroup) params.set("primary_group", primaryGroup);
  if (parentOfParentGroup) params.set("parent_of_parent_group", parentOfParentGroup);
  if (from) params.set("from_date", from);
  if (to) params.set("to_date", to);
  if (Array.isArray(compIds) && compIds.length) params.set("comp_ids", compIds.join(","));
  const { data } = await api.get(`/dash/bs/parent-groups?${params.toString()}`);
  return data;
};

export const getBsLedgersByGroup = async ({ line, primaryGroup, parentGroup, parentOfParentGroup, from, to, compIds = [] } = {}) => {
  const params = new URLSearchParams();
  if (line) params.set("line", line);
  if (primaryGroup) params.set("primary_group", primaryGroup);
  if (parentGroup) params.set("parent_group", parentGroup);
  if (parentOfParentGroup) params.set("parent_of_parent_group", parentOfParentGroup);
  if (from) params.set("from_date", from);
  if (to) params.set("to_date", to);
  if (Array.isArray(compIds) && compIds.length) params.set("comp_ids", compIds.join(","));
  const { data } = await api.get(`/dash/bs/ledgers-by-group?${params.toString()}`);
  return data;
};

export const getBsClosingBalanceVerification = async ({ companyName, from, to, compIds = [] } = {}) => {
  const params = new URLSearchParams();
  if (companyName) params.set("company_name", companyName);
  if (from) params.set("from_date", from);
  if (to) params.set("to_date", to);
  if (Array.isArray(compIds) && compIds.length) params.set("comp_ids", compIds.join(","));
  const { data } = await api.get(`/dash/bs/closing-balance-verification?${params.toString()}`);
  return data;
};

export const exportBsClosingBalanceVerification = async ({ companyName, from, to, compIds = [] } = {}) => {
  const params = new URLSearchParams();
  if (companyName) params.set("company_name", companyName);
  if (from) params.set("from_date", from);
  if (to) params.set("to_date", to);
  if (Array.isArray(compIds) && compIds.length) params.set("comp_ids", compIds.join(","));
  const response = await api.get(`/dash/bs/closing-balance-verification/export?${params.toString()}`, {
    responseType: 'blob'
  });
  return response.data;
};

const buildOpeningBalanceVerificationPayload = ({
  priorYearCompanyNames = [],
  currentYearCompanyNames = [],
  priorYearFrom,
  priorYearTo,
  currentYearFrom,
  currentYearTo,
  compIds = [],
} = {}) => ({
  prior_year_company_names: Array.isArray(priorYearCompanyNames) ? priorYearCompanyNames.filter(Boolean) : [],
  current_year_company_names: Array.isArray(currentYearCompanyNames) ? currentYearCompanyNames.filter(Boolean) : [],
  prior_year_from_date: priorYearFrom || undefined,
  prior_year_to_date: priorYearTo || undefined,
  current_year_from_date: currentYearFrom || undefined,
  current_year_to_date: currentYearTo || undefined,
  comp_ids: Array.isArray(compIds) ? compIds : [],
});

export const runOpeningBalanceVerification = async ({ signal, ...opts } = {}) => {
  const payload = buildOpeningBalanceVerificationPayload(opts);
  const { data } = await api.post("/dash/accuracy/opening-balance-verification/run", payload, { signal });
  return data;
};

export const exportOpeningBalanceVerification = async (opts = {}) => {
  const payload = buildOpeningBalanceVerificationPayload(opts);
  const response = await api.post("/dash/accuracy/opening-balance-verification/export", payload, {
    responseType: "blob",
  });
  return response.data;
};

export const getTbReconcilableCompanies = async () => {
  const { data } = await api.get("/dash/tb/reconcilable-companies");
  return data;
};

export const exportTbVerification = async ({ companyId, from, to, file } = {}) => {
  const form = new FormData();
  if (companyId != null) form.append("company_id", String(companyId));
  if (from) form.append("from_date", from);
  if (to) form.append("to_date", to);
  if (file) form.append("file", file);
  const response = await api.post("/dash/tb/verify-export", form, {
    responseType: "blob",
  });
  return {
    blob: response.data,
    contentDisposition: response.headers?.["content-disposition"] || "",
  };
};

export const getArApSummary = async ({ mode = "ar", from, to, compIds = [], signal } = {}) => {
  const params = new URLSearchParams();
  params.set("mode", mode);
  if (from) params.set("from_date", from);
  if (to) params.set("to_date", to);
  if (Array.isArray(compIds) && compIds.length) params.set("comp_ids", compIds.join(","));
  const { data } = await api.get(`/dash/arap/summary?${params.toString()}`, { signal });
  return data;
};

export const getArApSummaryCached = async (opts = {}, { ttlMs = 5 * 60 * 1000, onUpdate } = {}) => {
  const key = stableKey('arap_summary', { mode: opts.mode, from: opts.from, to: opts.to, compIds: opts.compIds });
  return staleWhileRevalidate({ key, ttlMs, onUpdate, signal: opts.signal, fetcher: () => getArApSummary(withoutSignal(opts)) });
};

export const getArApDetail = async ({ mode = "ar", from, to, compIds = [], signal } = {}) => {
  const params = new URLSearchParams();
  params.set("mode", mode);
  if (from) params.set("from_date", from);
  if (to) params.set("to_date", to);
  if (Array.isArray(compIds) && compIds.length) params.set("comp_ids", compIds.join(","));
  const { data } = await api.get(`/dash/arap/detail?${params.toString()}`, { signal });
  return data;
};

export const getArApDetailCached = async (opts = {}, { ttlMs = 5 * 60 * 1000, onUpdate } = {}) => {
  const key = stableKey('arap_detail', { mode: opts.mode, from: opts.from, to: opts.to, compIds: opts.compIds });
  return staleWhileRevalidate({ key, ttlMs, onUpdate, signal: opts.signal, fetcher: () => getArApDetail(withoutSignal(opts)) });
};

export const getArApAging = async ({ mode = "ar", basis = "invoice_date", from, to, compIds = [], signal } = {}) => {
  const params = new URLSearchParams();
  params.set("mode", mode);
  params.set("basis", basis);
  if (from) params.set("from_date", from);
  if (to) params.set("to_date", to);
  if (Array.isArray(compIds) && compIds.length) params.set("comp_ids", compIds.join(","));
  const { data } = await api.get(`/dash/arap/aging?${params.toString()}`, { signal });
  return data;
};

export const getArApAgingCached = async (opts = {}, { ttlMs = 5 * 60 * 1000, onUpdate } = {}) => {
  const key = stableKey('arap_aging', {
    mode: opts.mode,
    basis: opts.basis,
    from: opts.from,
    to: opts.to,
    compIds: opts.compIds,
  });
  return staleWhileRevalidate({ key, ttlMs, onUpdate, signal: opts.signal, fetcher: () => getArApAging(withoutSignal(opts)) });
};

export const getArApStatement = async ({ mode = "ar", ledger = "", company = "", from, to, compIds = [], signal } = {}) => {
  const params = new URLSearchParams();
  params.set("mode", mode);
  if (ledger) params.set("ledger", ledger);
  if (company) params.set("company", company);
  if (from) params.set("from_date", from);
  if (to) params.set("to_date", to);
  if (Array.isArray(compIds) && compIds.length) params.set("comp_ids", compIds.join(","));
  const { data } = await api.get(`/dash/arap/statement?${params.toString()}`, { signal });
  return data;
};

export const getArApStatementCached = async (opts = {}, { ttlMs = 5 * 60 * 1000, onUpdate } = {}) => {
  const key = stableKey('arap_statement', {
    mode: opts.mode,
    ledger: opts.ledger,
    company: opts.company,
    from: opts.from,
    to: opts.to,
    compIds: opts.compIds,
  });
  return staleWhileRevalidate({ key, ttlMs, onUpdate, signal: opts.signal, fetcher: () => getArApStatement(withoutSignal(opts)) });
};

export const getArApLedgerStatement = async ({ mode = "ar", ledger = "", company = "", to, compIds = [], signal } = {}) => {
  const params = new URLSearchParams();
  params.set("mode", mode);
  if (ledger) params.set("ledger", ledger);
  if (company) params.set("company", company);
  if (to) params.set("to_date", to);
  if (Array.isArray(compIds) && compIds.length) params.set("comp_ids", compIds.join(","));
  const { data } = await api.get(`/dash/arap/ledger-statement?${params.toString()}`, { signal });
  return data;
};

export const getArApLedgerStatementCached = async (opts = {}, { ttlMs = 5 * 60 * 1000, onUpdate } = {}) => {
  const key = stableKey('arap_ledger_statement', {
    mode: opts.mode,
    ledger: opts.ledger,
    company: opts.company,
    to: opts.to,
    compIds: opts.compIds,
  });
  return staleWhileRevalidate({ key, ttlMs, onUpdate, signal: opts.signal, fetcher: () => getArApLedgerStatement(withoutSignal(opts)) });
};
